/**
 * How synced records are addressed. S-07, L-05, and the v0.5 release gate.
 *
 * ## The problem this solves
 *
 * Sealed envelopes hide the *contents* of a record. They do nothing about where it
 * sits. A relay stores a graph, and it can read every node key it holds — so if
 * entries lived at `thana/entries/2026-08-17`, the relay would learn a complete
 * cycle history without decrypting a single byte. That is the same mistake the
 * local store made until v0.5 with its `dateIndex`, and it is worse here because
 * the observer is a third party rather than the device owner.
 *
 * So a soul is `HMAC-SHA-256(syncKey, "entry:" + date)`, base64url. The relay sees
 * an opaque 43-character string. A device holding the sync key can compute the
 * same soul for the same day, which is what makes two devices agree without ever
 * putting the date on the wire.
 *
 * ## Why HMAC and not a plain hash
 *
 * `sha256("entry:2026-08-17")` is the same for everybody. A relay could
 * precompute every date for the next fifty years — about eighteen thousand
 * hashes — and match them against the souls it stores. Keying the hash makes the
 * table useless without the key, and the key never leaves the device.
 *
 * ## What is still visible to a relay
 *
 * How many records an account has, roughly how large each is, and when each was
 * written. Traffic timing (T-08) cannot be fixed by naming, and is disclosed in
 * the README rather than glossed. What is NOT visible: which dates, and in what
 * order.
 */
import { fromBase64, toBase64 } from './crypto'

/**
 * Domain separation for the HMAC.
 *
 * Included so that a soul for an entry can never collide with a soul for
 * something else added later — settings, a symptom definition, an attachment.
 * Cheap now; impossible to retrofit once souls are in a relay's graph.
 */
const ENTRY_DOMAIN = 'entry:'

/** URL- and Gun-safe. Plain base64 contains `/` and `+`, which souls should not. */
function base64url(bytes: Uint8Array): string {
  return toBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Derive the soul-naming key from the sync key material.
 *
 * A separate HKDF-style derivation rather than reusing the record-sealing key:
 * naming and sealing are different purposes, and a key used for two things is a
 * key whose compromise costs twice. `info` fixes the purpose.
 */
export async function deriveSoulKey(syncKeyMaterial: Uint8Array): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey(
    'raw',
    syncKeyMaterial as unknown as BufferSource,
    'HKDF',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0) as unknown as BufferSource,
      info: new TextEncoder().encode('thana/soul-naming/v1'),
    },
    base,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
}

/**
 * The soul for one day's entry.
 *
 * Deterministic for a given key and date — two devices with the same sync key
 * compute the same soul, which is how they converge without the date ever
 * appearing on the wire.
 */
export async function soulForDate(date: string, soulKey: CryptoKey): Promise<string> {
  const mac = await crypto.subtle.sign(
    'HMAC',
    soulKey,
    new TextEncoder().encode(ENTRY_DOMAIN + date),
  )
  return base64url(new Uint8Array(mac))
}

/**
 * The graph path a sealed entry is written to.
 *
 * Two segments and nothing else. `e` is a fixed bucket name carrying no meaning —
 * deliberately not `entries`, not because the word is dangerous but because every
 * segment is something a relay reads, and a shorter vocabulary is a smaller
 * surface for a future mistake.
 *
 * Note what is absent: no account alias, no date, no device id, no field names.
 * The namespace map (`NAMESPACE-MAP.md`) records this as the only node Thana
 * writes, which is what makes the claim checkable rather than asserted.
 */
export function entryPath(accountPub: string, soul: string): string[] {
  return [`~${accountPub}`, 'e', soul]
}

/**
 * Everything a relay can observe about a written record.
 *
 * Exported so the disclosure in the README and the privacy policy is generated
 * from the same place the path is, rather than drifting from it.
 */
export interface RelayVisibleFacts {
  accountPub: string
  soul: string
  /** Byte length of the sealed envelope. */
  size: number
}

export function relayVisibleFacts(
  accountPub: string,
  soul: string,
  envelope: string,
): RelayVisibleFacts {
  return { accountPub, soul, size: envelope.length }
}

/**
 * Recover the raw bytes of a soul, for tests and diagnostics.
 *
 * Not a reversal — an HMAC cannot be reversed. Only the base64url decoding, so a
 * test can assert the soul is 32 bytes of MAC rather than something structured.
 */
export function soulBytes(soul: string): Uint8Array {
  const padded = soul.replace(/-/g, '+').replace(/_/g, '/')
  return fromBase64(padded + '='.repeat((4 - (padded.length % 4)) % 4))
}
